import { Component, Inject, OnDestroy, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { ProcedureDocument, DocumentNode } from '@completion/models';
import { Store } from '@ngrx/store';
import { getCurrentDocument } from '@completion/selectors';
import { NodeType } from '@completion/enums';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { parseLinkContentFromNode, ParsedLinkContent, toDataUri } from '../link/link-parser.util';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog';

@Component({
  selector: 'app-appendix',
  templateUrl: './appendix.component.html',
  styleUrls: ['./appendix.component.scss'],
  standalone: false,
})
export class AppendixComponent implements OnInit, OnDestroy, AfterViewInit {
  documentNode: DocumentNode; // appendix node itself
  procedureDocument: ProcedureDocument | null = null;
  links: DocumentNode[] = [];
  // width in px to pass to thumbnail component (calculated from container)
  appendixWidth = 800;
  @ViewChild('appendixWrapper', { static: false }) appendixWrapper!: ElementRef<HTMLElement>;
  private destroy$ = new Subject<void>();
  renderAllConfirmed = false;

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    @Inject('node') procedureDocument: ProcedureDocument,
    private store: Store,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.documentNode = documentNode;
    this.procedureDocument = procedureDocument;
  }

  ngOnInit(): void {
    // initial population if injected document available
    if (this.procedureDocument) {
      this.links = this.collectAppendixLinks(this.procedureDocument.nodes || []);
    }
    // subscribe for live updates
    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        if (doc) {
          this.links = this.collectAppendixLinks(doc.nodes || []);
        } else {
          this.links = [];
        }
      });
  }

  ngAfterViewInit(): void {
    // compute initial width
    this.updateWidth();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize') onWindowResize() {
    this.updateWidth();
  }

  trackLink(_index: number, item: DocumentNode): number {
    return item.id;
  }

  isPdf(lnk: DocumentNode): boolean {
    try {
      const parsed = parseLinkContentFromNode(lnk);
      const mime = parsed.mimeType || '';
      return mime.toLowerCase().includes('pdf');
    } catch {
      return false;
    }
  }

  // Return the parsed link content (cached per call). Keep lightweight and resilient.
  private getParsed(lnk: DocumentNode): ParsedLinkContent {
    try {
      return parseLinkContentFromNode(lnk);
    } catch {
      return { raw: lnk } as ParsedLinkContent;
    }
  }

  // Display string for the link (prefer parsed displayText, filename, then node name)
  getDisplayText(lnk: DocumentNode): string {
    const p = this.getParsed(lnk);
    return p.displayText || p.fileName || lnk?.name || 'Link';
  }

  // Return URL (secureUrl) or data: URI for files/images; null if none
  getFileHref(lnk: DocumentNode): string | null {
    const p = this.getParsed(lnk);
    if (p.secureUrl) return p.secureUrl;
    if (p.base64) return toDataUri(p.base64, p.mimeType || undefined);
    return null;
  }

  // For images return a usable src (secureUrl or data URI)
  getImageSrc(lnk: DocumentNode): string | null {
    const p = this.getParsed(lnk);
    if (p.mimeType && p.mimeType.startsWith('image/') && p.base64) return toDataUri(p.base64, p.mimeType);
    if (p.secureUrl && p.mimeType && p.mimeType.startsWith('image/')) return p.secureUrl;
    // fallback: if secureUrl exists and extension looks like an image
    const href = p.secureUrl || null;
    return href && /\.(png|jpe?g|gif|webp|svg)$/i.test(href) ? href : null;
  }

  // Extract a friendly filename or path for display in the PDF block
  getFileName(lnk: DocumentNode): string | null {
    try {
      const parsed = this.getParsed(lnk);
      return parsed.fileName || null;
    } catch {
      return null;
    }
  }

  private collectAppendixLinks(nodes: DocumentNode[]): DocumentNode[] {
    const out: DocumentNode[] = [];
    const walk = (list: DocumentNode[]) => {
      for (const n of list || []) {
        // Use the shared parser which normalizes content and top-level props
        try {
          const parsed = parseLinkContentFromNode(n);
          if (n.type === NodeType.LINK && !!parsed.embedInAppendix) {
            out.push(n);
          }
        } catch {
          // fallback: top-level flag
          if (n.type === NodeType.LINK && !!(n as DocumentNode & { embedInAppendix?: boolean }).embedInAppendix) {
            out.push(n);
          }
        }
        if (n.children && n.children.length) {
          walk(n.children);
        }
      }
    };
    walk(nodes || []);
    return out;
  }

  parsePdfBase64(lnk: DocumentNode): string | null {
    try {
      const parsed = parseLinkContentFromNode(lnk);
      if (!parsed) return null;
      if (parsed.base64) return parsed.base64.startsWith('data:') ? parsed.base64 : `data:application/pdf;base64,${parsed.base64}`;
      return null;
    } catch {
      return null;
    }
  }

  private updateWidth() {
    try {
      const el = this.appendixWrapper?.nativeElement;
      if (el) {
        // account for some padding/margins
        this.appendixWidth = Math.max(200, el.clientWidth - 20);
        this.cd.detectChanges();
      }
    } catch {
      /* ignore */
    }
  }

  confirmRenderAll(): void {
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Render all PDF pages',
        message: 'Rendering all pages may be slow or resource intensive. Do you want to continue?',
        isCancelDisplay: true,
        confirmationTile: 'Got it',
      },
    });
    ref.afterClosed().subscribe((result) => {
      this.renderAllConfirmed = !!result;
    });
  }
}
